import { Component, ElementRef, Inject, Input, NgZone, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import ExcelJS from 'exceljs';
import { ProductsService } from '../../../../core/services/products.service';
import { AddProductsService } from '../../../../core/services/add-products.service';
import { Utils } from '../../../../core/services/utils.service';
import { Product, ProductParams } from '../../../models/product';
import { ScrollToDirective } from '../../../../core/directives/scroll-to.directive';
import { FileSystemFileEntry, NgxFileDropEntry } from 'ngx-file-drop';
import { lastValueFrom, Observable, of } from 'rxjs';
import { IAddProductsResult } from '../../../../core/models/addProductsTableItem';
import { ProductGroupRotation } from '../../../models/product-group-rotation';
import { catchError } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { SnackBarService } from '../../../../core/services/snack-bar.service';

type ExcelRowData = Array<string>;
type ExcelTableData = Array<ExcelRowData>;

@Component({
    selector: 'excel-to-layout',
    templateUrl: './excel-to-layout.component.html',
    styleUrls: ['./excel-to-layout.component.scss'],
    standalone: false
})
export class ExcelToLayoutComponent {
	not_found: any = {};
	duplicated_rows: any = {};
	invalid_value: any = {};
	duplicated_rows_height: any = {};
	origin_products: any = [];
	table_data: any[] = [];
	errors_list: any = {};
	show_table: boolean = false;
	loader: boolean = false;
	show_delete_duplicate_btn: boolean = false;
	position: string = 'above';
	errors_count: number;
	current_error_id: number;

	@Input('scroll_to_row') scroll_to_row: number;

	@ViewChild('table') table: ElementRef;
	@ViewChild(ScrollToDirective) scrollToDirective;

	constructor(
		private dialogRef: MatDialogRef<ExcelToLayoutComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any,
		private addProductsService: AddProductsService,
		private Products: ProductsService,
		private snackBarService: SnackBarService,
		private ngZone: NgZone
	) {}

	static isEmpty(row) {
		return row.every(i => !i);
	}

	canAddProduct(article, shelfId: string, placement_rows) {
		return shelfId &&
			!placement_rows.some(isNaN) &&
			placement_rows.filter(i => i).length === placement_rows.length &&
			this.origin_products.find(i => i.article == article);
	}

	chooseFile(files: Array<NgxFileDropEntry>): void {
		if (!files.length) return;
		
		const fileName: string = files[0].fileEntry.name;
		const fileExtension: string = fileName.split('.').slice(-1)[0]?.toLowerCase() ?? '';
		if (!['xlsx', 'xls'].includes(fileExtension)) return;

		this.loader = true;

		const fileEntry: FileSystemFileEntry = files[0].fileEntry as FileSystemFileEntry;
		fileEntry.file((file: File): void => {
			const reader: FileReader = new FileReader();

			reader.onload = (e: ProgressEvent<FileReader>): void => {
				this.ngZone.run(async (): Promise<void> => {
					try {
						const workbook: ExcelJS.Workbook = new ExcelJS.Workbook();
						await workbook.xlsx.load(e.target.result as ArrayBuffer);
						const worksheet: ExcelJS.Worksheet | undefined = workbook.worksheets[0];

						const tableData: ExcelTableData = [];
						worksheet.eachRow((row: ExcelJS.Row): void => {
							const rowData: ExcelRowData = [];
							row.eachCell({ includeEmpty: true }, (cell: ExcelJS.Cell, colNumber: number): void => {
								const cellValue: ExcelJS.CellValue = cell.value;
								rowData[colNumber - 1] = cellValue !== null && cellValue !== undefined ? String(cellValue) : '';
							});
							tableData.push(rowData);
						});

						// remove empty rows
						this.table_data = tableData.filter((row: ExcelRowData): boolean => !ExcelToLayoutComponent.isEmpty(row));

						this.table_data.forEach((row: ExcelRowData, idx: number): void => {
							// append product name into sheet
							if (!idx) {
								row.splice(2, 0, 'Product name');
								return;
							}
							row.splice(2, 0, '');
						});

						this.checkEqualProductsOnShelf(this.table_data);
						this.checkInvalidValue();

						this.checkProductsExists()
							.then((value: boolean): void => {
								this.loader = false;
								if (value) {
									this.countErrors();
									this.show_table = true;
									this.dialogRef.updateSize('70%', '80%');
								}
							});
					} catch (error: unknown) {
						this.loader = false;
						this.snackBarService.setErrorNotification();
					}
				});
			};

			reader.readAsArrayBuffer(file);
		});
	}

	async onChangeTd(p_idx, c_idx, e) {
		const clear_table = [2];

		clear_table.forEach(i => {
			if (c_idx == 3) {
				this.table_data[p_idx][i] = '';
			}
		});

		this.table_data[p_idx][c_idx] = e.target.value;

		if (!e.target.value && c_idx != 6) {
			this.not_found[p_idx] = true;
			return;
		} else if (!e.target.value && c_idx == 6) {
			this.updateErrorsList();
			return;
		} else {
			this.not_found[p_idx] = false;
		}

		this.checkEqualProductsOnShelf(this.table_data);
		return this.checkProductsExists([e.target.value]).then(() => this.loader = false);
	}

	async checkProductsExists(changedArticles?: any): Promise<boolean> {
		this.not_found = {};
		const table_articles: Array<any> = this.table_data
			.slice(1)
			.map((i: any): any => i[3].toString())
			.filter((i: any): any => i);

		let all_p: Array<ProductParams> = await this.Products.exist(changedArticles || table_articles);
		let fetchedProducts: IAddProductsResult | null = null;

		if (all_p.length > 0) {
			fetchedProducts = await lastValueFrom(
				this.addProductsService.getProducts(
					{
						key: 'product_id=',
						plan_id: this.data.planogram,
						planogram_id: this.data.stand.id
					},
					{
						ids: all_p.map((p: ProductParams): number => p.id).join(',')
					}
				).pipe(
					catchError((error: HttpErrorResponse): Observable<null> => {
						this.snackBarService.setErrorNotification();
						return of(null);
					})
				)
			);
		}

		if (fetchedProducts === null && all_p.length > 0) {
			return false;
		}

		if (fetchedProducts?.results) {
			all_p = fetchedProducts?.results;
		} else {
			all_p = [];
		}

		const diff: Array<string> = Utils.arrayDiff<string>(changedArticles || table_articles, all_p.map((i: ProductParams): string => i.article));

		if (diff.length && !changedArticles && table_articles.length > 0 && all_p.length === 0) {
			this.snackBarService.setSnackBarNotification({
				messageText: 'PLANOGRAM_EDIT.NO_PRODUCTS_FROM_FILE'
			});
			return false;
		}

		if (diff.length && !changedArticles) {
			diff.forEach((article: string): void => {
				const indexes: any = this.table_data.reduce((a: any, e: any, i: number): any => {
					if (e[3] == article) a[i] = true;
					return a;
				}, {});

				Object.assign(this.not_found, indexes);
			});
		}

		if (diff.length && changedArticles) {
			const idx: number = this.table_data.findIndex((i: any): any => changedArticles.includes(i[3]));
			this.not_found[idx] = true;
		}
		// set product names to existing products
		all_p.forEach((product: ProductParams): void => {
			this.table_data.forEach((row: any): void => {
				if (row[3] == product.article) {
					row[2] = product.name;
				}
			});
		});

		this.origin_products = this.origin_products.concat(all_p.map((p: ProductParams): Product => new Product(p)));
		this.origin_products = this.origin_products.filter((product: Product, index: number, array: Array<Product>): boolean =>
			table_articles.includes(product.article) && array.findIndex((p: Product): boolean => p.id === product.id) === index
		);
		await this.updateErrorsList();
		return true;
	}

	checkNotFoundErrors() {
		this.not_found = {};
		this.table_data.forEach((row, idx) => {
			if (!row[2]) this.not_found[idx] = true;
		});
	}

	checkEqualProductsOnShelf(data) {
		this.duplicated_rows = {};

		const duplicatedRows = Utils.findDuplicates(data.map(row => row[0] + ',' + row[1] + ',' + row[3])),
			duplicatedRowsIdx = this.table_data
				.map(row => (row[0] + ',' + row[1] + ',' + row[3]).split(','))
				.map((el, idx) => {
					if (duplicatedRows.map(el => el.join()).includes(el.join())) {
						return idx;
					}
				})
				.filter(r => r);
		duplicatedRowsIdx.forEach(item => {
			const indexes = this.table_data.reduce((a, e, i) => {
				if (i == item) a[i] = true;
				return a;
			}, {});

			Object.assign(this.duplicated_rows, indexes);
		});
	}

	getErrorsPosition(data) {
		const errorRowsIdx = Object.keys(this.errors_list).map(el => Number(el)),
			duplicateRowsIdx = Object.keys(this.duplicated_rows).map(el => Number(el));
		if (!this.current_error_id) {
			this.current_error_id = errorRowsIdx[0];
		} else {
			const currentErrorIdx = errorRowsIdx.findIndex(el => el == this.current_error_id);
			this.current_error_id = currentErrorIdx + 1 < errorRowsIdx.length ?
				errorRowsIdx[currentErrorIdx + 1] : errorRowsIdx[0];
		}

		this.show_delete_duplicate_btn = duplicateRowsIdx.includes(this.current_error_id);
		this.scrollToDirective.scrollTable(this.current_error_id);
	}

	async deleteDuplicate() {
		this.table_data = this.table_data.filter((el, idx) => idx !== this.current_error_id);
		this.updateErrorsList();
		this.table.nativeElement.scrollTop = 0;
		this.show_delete_duplicate_btn = false;
		this.current_error_id = null;
	}

	checkInvalidValue() {
		this.invalid_value = {};
		this.table_data.forEach((row, idx) => {
			if (!idx) return;
			const reqIdx = [4, 5, 7];
			if (!reqIdx.every(i => row[i] && row[i] > 0) || (row[6] && row[6] <= 0)) this.invalid_value[idx] = true;
		});
	}

	getTooltipText(p_idx, c_idx, value) {
		const reqIdx = [4, 5, 7],
			validIdx = [4, 5, 6, 7];
		return this.not_found[p_idx] && c_idx == 3 ? 'PRODUCT_DOESNT_FOUND' :
			((!value && reqIdx.includes(c_idx)) || (value && value <= 0 && validIdx.includes(c_idx))) ?
				'UNABLE_VALUE' : '';
	}

	async updateErrorsList() {
		this.checkEqualProductsOnShelf(this.table_data);
		this.checkNotFoundErrors();
		this.checkInvalidValue();
		this.countErrors();
	}

	countErrors() {
		// this.errors_list = {};
		this.errors_list = Object.assign({}, this.not_found, this.duplicated_rows, this.invalid_value);
		this.errors_count = Object.values(this.errors_list).filter(el => el).length;
	}

	disableButton() {
		return this.errors_count > 0;
	}

	onNoClick(): void {
		this.dialogRef.close(false);
	}

	async apply() {
		if (this.errors_count > 0) return;

		const products = [];

		this.data.stand.stand.bases.forEach((base, base_idx) => {
			const shelves = this.data.stand.stand
					.availableElements
					.filter(i => i.constructor.name === 'ShelfElement' && (i.getDepends().includes(base) || i == base))
					.sort((a, b) => a.shapes[0].begin.y - b.shapes[0].begin.y),

				// filter products by instrument
				stand_products = this.table_data
					.slice(1)
					.filter(i => i[0] == base_idx + 1);

			shelves.forEach((shelf, s_idx) => {
				const zGroups = [],
					shelf_products = stand_products.filter(row => row[1] == s_idx + 1);

				shelf_products.filter(item => {
					const product = this.origin_products.find(i => i.article == item[3]);
					return !product.is_loose;
				}).forEach((row, idx) => {
					if (row[5] == 1 || !idx) {
						zGroups.push([row]);
					} else {
						zGroups[zGroups.length - 1].push(row);
					}
				});

				const origin_idx = zGroups.map(g => g.map((i, idx) => idx));

				zGroups.forEach((g, g_idx) => g.sort((a, b) => {
					const result = a[5] - b[5];

					if (!result) {
						const a_idx = g.indexOf(a),
							b_idx = g.indexOf(b);

						return origin_idx[g_idx][a_idx] - origin_idx[g_idx][b_idx];
					}

					return result;
				}));

				zGroups.forEach(group => group.forEach((g, idx) => {
					g[5] = idx + 1;
					g[8] = group.length;
				}));

				let nextX = shelf.layoutBoundingX.min, nextZ = shelf.layoutBoundingZ.max;
				(zGroups as any).flat().forEach((row, idx) => {
					const shelfId = this.data.stand.stand.allElements.find((el) => el == shelf)?.id,
						shelfIndex = this.data.stand.stand.allElements.indexOf(shelf),
						placement_rows = [row[4], row[7]]; // depth, height, width

					// return if there are no shelf or invalid placement params or product doesn't exist in database
					if (!this.canAddProduct(row[3], shelfId, placement_rows)) return;

					// current position x = prev_product_position_x + prev_product_width * prev_product_faces_qty
					const product = this.origin_products.find(i => i.article == row[3]),
						position_x = row[5] > 1 ? products[products.length - 1].position_x : nextX,
						position_y = Utils.round(shelf.shapes[0].begin.y + shelf.shapes[0].height),
						position_z = row[5] == 1 ? shelf.layoutBoundingZ.max : nextZ,
						bw = shelf.getDepends().find(i => ['BackWall'].includes(i.parent.type)),
						bw_depth = bw ? bw.shapes[0].depth : 0,
						freeSpace = shelf.shapes[0].depth - bw_depth,
						productSpace = freeSpace / row[8],
						placement_depth = row[6] || Math.floor(productSpace / product.depth) || 1;

					//dont push if product is weight
					if (!product.is_loose) {
						products.push({
							stand: this.data.stand.id,
							element_id: shelfId,
							shelf_no: shelfIndex,
							qty: row[4] * placement_depth,
							master_product: product.id,
							rotation: ProductGroupRotation.Front,
							position_x,
							position_y,
							position_z,
							placement_depth,
							placement_height: row[7],
							packing: null
						});
					}

					nextX = Math.max(nextX, Utils.round(position_x + product.width * row[4]));
					nextZ = Utils.round(position_z - product.depth * placement_depth);
				});
			});
		});

		return this.dialogRef.close({ products, origin_products: this.origin_products });
	}
}
